import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Wrapper from '../../components/Wrapper';
import CsrKpiCard from '../../components/CsrDashboard/CsrKpiCard';
import ImpactSignalCards from '../../components/CsrDashboard/ImpactSignalCards';
import IndiaStateMap from '../../components/CsrDashboard/IndiaStateMap';
import DistrictImpactPanel from '../../components/CsrDashboard/DistrictImpactPanel';
import NoDataStateNotice from '../../components/CsrDashboard/NoDataStateNotice';
import { CSR_IMPACT_PLACEHOLDERS } from '../../components/CsrDashboard/csrPlaceholders';
import { INDIA_STATES, stateKey } from '../../components/CsrDashboard/indiaStatePaths';
import {
  fetchCsrStateCoverage,
  fetchDashboardMetrics,
  fetchPartnerDashboardForAdmin,
} from '../../services/dashboardApi';
import '../../styles/csrDashboard.css';

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Math.round(Number(n)).toLocaleString('en-IN'));
const titleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const coverageErrorMessage = (err) => {
  const status = err?.response?.status;
  if (status === 403) return 'The CSR Impact dashboard is available for CSR and NGO partners only.';
  if (status === 404) return 'This partner could not be found.';
  return err?.response?.data?.message || 'Could not load the CSR Impact dashboard.';
};

const CsrOverview = () => {
  // Support opens /csr/overview/:adminId; partners and team members use their own login scope.
  const { adminId } = useParams();

  const [coverage, setCoverage] = useState(null);
  const [coverageStatus, setCoverageStatus] = useState('loading'); // loading | success | error
  const [coverageError, setCoverageError] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [metricsStatus, setMetricsStatus] = useState('loading');

  const [scope, setScope] = useState('all'); // 'all' or a stateKey
  const [district, setDistrict] = useState(null);
  const [layer, setLayer] = useState('tests');
  const [bars, setBars] = useState('both');

  const loadCoverage = useCallback(async () => {
    setCoverageStatus('loading');
    try {
      const data = await fetchCsrStateCoverage(adminId);
      setCoverage(data);
      setCoverageStatus('success');
    } catch (err) {
      setCoverageError(coverageErrorMessage(err));
      setCoverageStatus('error');
    }
  }, [adminId]);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  // ── derived coverage ──
  const activeStates = useMemo(
    () => (coverage?.active_states || []).map((s) => ({ ...s, key: stateKey(s.state) })),
    [coverage]
  );
  const coverageByKey = useMemo(() => new Map(activeStates.map((s) => [s.key, s])), [activeStates]);
  const stateOptions = useMemo(() => {
    const known = new Set(INDIA_STATES.map((s) => stateKey(s.name)));
    const extra = activeStates.filter((s) => !known.has(s.key)).map((s) => ({ key: s.key, name: s.state }));
    return [...INDIA_STATES.map((s) => ({ key: stateKey(s.name), name: s.name })), ...extra];
  }, [activeStates]);

  const scopeRow = scope === 'all' ? null : coverageByKey.get(scope) || null;
  const isStateScope = Boolean(scopeRow);
  const isNoDataScope = scope !== 'all' && !scopeRow;
  const scopeName = scope === 'all'
    ? 'All India'
    : scopeRow?.state || stateOptions.find((o) => o.key === scope)?.name || scope;

  // ── KPI metrics (existing dashboard-metrics API, filtered by state) ──
  const metricsState = scopeRow?.state;
  useEffect(() => {
    if (coverageStatus !== 'success' || isNoDataScope) return undefined;
    let ignore = false;
    const params = metricsState ? { state: metricsState } : {};
    setMetricsStatus('loading');
    (adminId ? fetchPartnerDashboardForAdmin(adminId, params) : fetchDashboardMetrics(params))
      .then((data) => {
        if (ignore) return;
        setMetrics(Object.fromEntries((data?.metrics || []).map((m) => [m.key, m.count])));
        setMetricsStatus('success');
      })
      .catch(() => {
        if (ignore) return;
        setMetrics(null);
        setMetricsStatus('error');
      });
    return () => { ignore = true; };
  }, [adminId, coverageStatus, isNoDataScope, metricsState]);

  const selectState = (key) => { setScope(key); setDistrict(null); };
  const resetScope = () => { setScope('all'); setDistrict(null); };

  // ── KPI values ──
  const districtsCovered = isStateScope
    ? scopeRow.districts_covered
    : activeStates.reduce((n, s) => n + s.districts_covered, 0);
  const crops = isStateScope
    ? scopeRow.crops
    : [...new Set(activeStates.flatMap((s) => s.crops))].sort();
  const cropSub = crops.length
    ? `${crops.slice(0, 3).map(titleCase).join(', ')}${crops.length > 3 ? ` +${crops.length - 3}` : ''}`
    : 'none recorded yet';
  const metric = (key) => (metricsStatus === 'success' ? fmt(metrics?.[key]) : metricsStatus === 'loading' ? '…' : '—');

  const kpis = [
    { label: 'Soil tests completed', value: metric('soilReports'), sub: '16 parameters each' },
    { label: 'Farmers reached', value: metric('farmers'), sub: 'registered with this partner' },
    { label: 'Acres covered', value: metric('totalArea'), sub: 'under registered farms' },
    { label: 'Districts covered', value: fmt(districtsCovered), sub: isStateScope ? `in ${scopeName}` : `across ${plural(activeStates.length, 'active state', 'active states')}` },
    { label: 'Crops covered', value: fmt(crops.length), sub: cropSub },
    { label: 'Livelihoods created', value: metric('fieldOfficers'), sub: isStateScope ? `field officers based in ${scopeName}` : 'field officers' },
  ];

  const districtsInScope = isStateScope ? (coverage?.districts || []).filter((d) => stateKey(d.state) === scope) : [];
  const unspecified = coverage?.unspecified_state || { farmers: 0, soil_tests: 0 };
  const hasNoData = coverageStatus === 'success' && activeStates.length === 0 && !unspecified.farmers && !unspecified.soil_tests;
  const partner = coverage?.partner;

  const eyebrow = isNoDataScope
    ? `${scopeName} · no data yet`
    : isStateScope ? scopeName : `All India · ${plural(activeStates.length, 'active state', 'active states')}`;

  return (
    <Wrapper>
      <div className="csr-dash">
        <div className="csr-wrap">
          <header className="csr-header">
            <div className="csr-header__text">
              <span className="csr-eyebrow"><span className="csr-dot" />{coverageStatus === 'success' ? eyebrow : 'CSR & NGO partner dashboard'}</span>
              <h1 className="csr-title">CSR Impact Overview</h1>
              <p className="csr-sub">
                <span>{`Reach, soil testing and state coverage${partner?.entity_name ? ` for ${partner.entity_name}` : ''}.`}</span>
                {adminId && partner?.category ? <span className="csr-chip">{partner.category.toUpperCase()}</span> : null}
              </p>
            </div>
            {coverageStatus === 'success' ? (
              <div className="csr-controls">
                <label htmlFor="csr-state">State</label>
                <select
                  id="csr-state"
                  className={`csr-select ${isNoDataScope ? 'is-nodata' : ''}`}
                  value={scope}
                  onChange={(e) => selectState(e.target.value)}
                >
                  <option value="all">All India</option>
                  {stateOptions.map((o) => (
                    <option key={o.key} value={o.key}>{coverageByKey.has(o.key) ? o.name : `${o.name} · no data`}</option>
                  ))}
                </select>
                <span className="csr-badge">Impact metrics pending</span>
              </div>
            ) : null}
          </header>

          {coverageStatus === 'loading' ? (
            <div aria-busy="true" aria-label="Loading CSR Impact dashboard" className="d-flex flex-column" style={{ gap: 20 }}>
              <div className="csr-kpis">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="csr-skel" style={{ height: 96 }} />)}</div>
              <div className="csr-skel" style={{ height: 180 }} />
              <div className="csr-skel" style={{ height: 520 }} />
            </div>
          ) : null}

          {coverageStatus === 'error' ? (
            <section className="csr-card csr-notice" role="alert">
              <span className="csr-chip csr-chip--amber csr-chip--caps">Unavailable</span>
              <h2>Dashboard could not be loaded</h2>
              <p>{coverageError}</p>
              <button type="button" className="csr-btn" onClick={loadCoverage}>Retry</button>
            </section>
          ) : null}

          {coverageStatus === 'success' ? (
            <div className="csr-reveal d-flex flex-column" style={{ gap: 20 }}>
              {hasNoData ? (
                <p className="csr-inline-warn">No farmers or soil tests are recorded for this partner yet. The dashboard fills in automatically as data arrives.</p>
              ) : null}

              {isNoDataScope ? (
                <NoDataStateNotice
                  stateName={scopeName}
                  activeStateNames={activeStates.map((s) => s.state)}
                  onReset={resetScope}
                />
              ) : (
                <>
                  <section aria-label="State coverage summary" className="d-flex flex-column" style={{ gap: 8 }}>
                    <div className="csr-kpis">
                      {kpis.map((k) => <CsrKpiCard key={k.label} {...k} />)}
                    </div>
                    {metricsStatus === 'error' ? (
                      <p className="csr-inline-warn">Some figures could not be loaded right now. Refresh the page to try again.</p>
                    ) : null}
                  </section>
                  <ImpactSignalCards impact={CSR_IMPACT_PLACEHOLDERS} />
                </>
              )}

              <div className="csr-split">
                <IndiaStateMap
                  coverage={coverageByKey}
                  scopeKey={scope}
                  layer={layer}
                  onLayerChange={setLayer}
                  onSelectState={selectState}
                  onReset={resetScope}
                  unspecifiedFarmers={unspecified.farmers}
                />
                {!isNoDataScope ? (
                  <DistrictImpactPanel
                    scopeName={scopeName}
                    isStateScope={isStateScope}
                    activeStates={activeStates}
                    districts={districtsInScope}
                    selectedDistrict={district}
                    onSelectState={selectState}
                    onSelectDistrict={setDistrict}
                    onClearDistrict={() => setDistrict(null)}
                    bars={bars}
                    onBarsChange={setBars}
                    score={CSR_IMPACT_PLACEHOLDERS.soilScore}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
};

export default CsrOverview;